// packages/ui/src/tools/InstanceHost.tsx
import {
  Suspense,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type Ref,
} from 'react';
import type { ToolFocusHandle, ToolHost } from '@slate/core';
import type { ToolDefinition, ToolModule, ToolViewProps } from './types';
import { useToolsStore, type ToolInstance } from './toolsStore';

export interface InstanceHostProps {
  instance: ToolInstance;
  def: ToolDefinition;
  host: ToolHost;
  focusToolRef?: Ref<ToolFocusHandle>;
}

export function InstanceHost({
  instance,
  def,
  host,
  focusToolRef,
}: InstanceHostProps): ReactElement {
  const [mod, setMod] = useState<ToolModule | null>(null);
  const [state, setState] = useState<unknown>(undefined);

  useEffect(() => {
    let cancelled = false;
    void def.load().then((loaded) => {
      if (cancelled) return;
      setMod(loaded);
      const init =
        loaded.createState?.(host, instance.initialState as never) ??
        instance.initialState;
      setState(init);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [def.id, instance.id]);

  const setTitle = useMemo(
    () => (title: string) =>
      useToolsStore.getState().setInstanceTitle(instance.id, title),
    [instance.id],
  );

  if (!mod) {
    return <div className="mp-tool-loading">Loading {def.title}…</div>;
  }

  const Component = mod.component;
  const props: ToolViewProps = {
    instanceId: instance.id,
    state: state as never,
    setState: (next) =>
      setState((prev: unknown) =>
        typeof next === 'function'
          ? (next as (p: unknown) => unknown)(prev)
          : next,
      ),
    host,
    setTitle,
    focusToolRef,
  };
  return (
    <Suspense fallback={<div className="mp-tool-loading">Loading…</div>}>
      <Component {...props} />
    </Suspense>
  );
}
